<template>
  <page-layout :title="title">
    <template v-slot:breadcrumb>
      <li><router-link :to="{ name: 'home' }">Dashboard</router-link></li>
      <li>
        <router-link :to="{ name: 'spotTracker.list' }"> Balises SPOT </router-link>
      </li>
      <li class="is-active">
        <a href="#">{{ title }}</a>
      </li>
    </template>
    <div class="columns">
      <div class="column is-full-mobile is-6 ml-auto">
        <spot-tracker-search />
      </div>
    </div>
    <div v-if="spotTracker">
      <div class="tile is-ancestor">
        <div class="tile is-parent">
          <div class="tile is-child card">
            <div class="card-header">
              <h2 class="card-header-title">Informations</h2>
              <div class="my-auto mx-3 is-flex is-align-items-center">
                <b-tooltip
                  :label="wears.find((wear) => wear.value === spotTracker.wear).label"
                  type="is-light"
                  position="is-bottom"
                >
                  <b-icon
                    :icon="wearIcons[spotTracker.wear].icon"
                    :type="wearIcons[spotTracker.wear].type"
                  />
                </b-tooltip>
                <b-tag :type="tags[spotTracker.availability].type" class="ml-2">
                  {{ tags[spotTracker.availability].label }}
                </b-tag>
              </div>
            </div>
            <div class="card-content">
              <table class="table is-striped is-fullwidth">
                <tr>
                  <th>Numéro</th>
                  <td>{{ spotTracker.number }}</td>
                </tr>
                <tr>
                  <th>ESN</th>
                  <td>{{ spotTracker.serialNumber }}</td>
                </tr>
                <tr>
                  <th>Auth</th>
                  <td>{{ spotTracker.authorizationCode }}</td>
                </tr>
                <tr>
                  <th>Date d'achat</th>
                  <td>{{ formatDate(spotTracker.purchasedAt) }}</td>
                </tr>
                <tr>
                  <th>Date d'expiration</th>

                  <td
                    :class="
                      isPast(spotTracker.expiredAt) ? 'has-text-danger has-text-weight-medium' : ''
                    "
                  >
                    {{ formatDate(spotTracker.expiredAt) }}
                  </td>
                </tr>
                <tr>
                  <th>Locations depuis l'achat</th>
                  <td>{{ lives.length }}</td>
                </tr>
                <tr>
                  <th>Locations depuis l'abonnement annuel</th>
                  <td>{{ totalLivesFromExpiredAt }}</td>
                </tr>
                <tr>
                  <th>Version du firmware</th>
                  <td>{{ spotTracker.firmwareVersion || '–' }}</td>
                </tr>
                <tr>
                  <th>Dernier point relevé</th>
                  <td>
                    <span v-if="showLastKnownLocationInformations">{{
                      formatDateWithTime(lastKnownLocation?.locatedAt ?? '')
                    }}</span>
                    <span v-else>–</span>
                  </td>
                </tr>
                <tr>
                  <th>Dernier niveau de batterie</th>
                  <td>
                    <o-battery-level-indicator
                      v-if="showLastKnownLocationInformations"
                      :battery-percentage="lastKnownLocation.batteryLevel"
                    />
                    <span v-else>–</span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
        <div class="tile is-parent">
          <div class="tile is-child card">
            <spot-tracker-map class="card" :lastKnownLocation="lastKnownLocation" />
          </div>
        </div>
      </div>
      <div class="tile is-ancestor">
        <div class="tile is-parent">
          <div class="tile is-child card">
            <div class="card-header">
              <h2 class="card-header-title">Changement d'état</h2>
            </div>
            <div class="card-content">
              <spot-tracker-edit-form :spotTracker.sync="spotTracker" />
            </div>
          </div>
        </div>
      </div>
      <div class="tile is-ancestor">
        <div class="tile is-parent">
          <div class="tile is-child card">
            <div class="card-header">
              <h2 class="card-header-title">Lives</h2>
            </div>
            <div class="card-content">
              <spot-tracker-live-table :lives="formattedLives" :loading="loading" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </page-layout>
</template>

<script>
import { format, isPast, isWithinInterval, subYears } from 'date-fns'
import PageLayout from '@components/PageLayout.vue'
import SpotTrackerMap from './SpotTrackerMap.vue'
import SpotTrackerSearch from './SpotTrackerSearch.vue'
import SpotTrackerEditForm from './SpotTrackerEditForm.vue'
import SpotTrackerLiveTable from './SpotTrackerLiveTable.vue'
import { TAGS } from '@constants/tracker/tag'
import { WEARS, WEAR_ICONS } from '@constants/tracker/wear'
import OBatteryLevelIndicator from '@components/BatteryLevelIndicator.vue'

export default {
  name: 'EditSpotTrackerPage',

  components: {
    OBatteryLevelIndicator,
    SpotTrackerMap,
    SpotTrackerSearch,
    SpotTrackerLiveTable,
    [PageLayout.name]: PageLayout,
    [SpotTrackerEditForm.name]: SpotTrackerEditForm,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      spotTracker: null,
      lives: [],
      liveVehicles: [],
      lastKnownLocation: null,
    }
  },

  created() {
    this.tags = TAGS
    this.wears = WEARS
    this.wearIcons = WEAR_ICONS
  },

  watch: {
    id: {
      handler: 'load',
      immediate: true,
    },
  },

  computed: {
    title() {
      return this.spotTracker ? `SPOT ${this.spotTracker.number}` : '...'
    },

    showLastKnownLocationInformations() {
      return this.lastKnownLocation !== null
    },

    totalLivesFromExpiredAt() {
      return this.lives.filter(
        (live) =>
          this.spotTracker.expiredAt &&
          (isWithinInterval(live.startedAt, {
            start: subYears(this.spotTracker.expiredAt, 1),
            end: this.spotTracker.expiredAt,
          }) ||
            isWithinInterval(live.endedAt, {
              start: subYears(this.spotTracker.expiredAt, 1),
              end: this.spotTracker.expiredAt,
            }) ||
            isWithinInterval(this.spotTracker.expiredAt, {
              start: live.startedAt,
              end: live.endedAt,
            })),
      ).length
    },

    formattedLives() {
      return this.lives.map((live) => ({
        ...live,
        vehicleId: this.liveVehicles[live.id]?.id,
        vehicleNumber: this.liveVehicles[live.id]?.number,
      }))
    },
  },

  methods: {
    load: async function () {
      this.loading = true

      await Promise.all([
        this.fetchSpotTracker(),
        this.fetchAllLivesForSpotTracker(),
        this.fetchLiveVehicles(),
        this.fetchLastKnownLocation(),
      ])

      this.loading = false
    },

    async fetchSpotTracker() {
      try {
        this.spotTracker = await this.$services.spotTrackerService.getById(this.$props.id)
      } catch {
        this.spotTracker = null
      }
    },

    async fetchAllLivesForSpotTracker() {
      try {
        this.lives = await this.$services.liveService.getAllBySpotTrackerId(this.$props.id)
      } catch {
        this.lives = []
      }
    },

    async fetchLiveVehicles() {
      try {
        this.liveVehicles = (
          await this.$services.liveVehicleService.getAllBySpotTrackerId(this.$props.id)
        ).reduce(
          (vehicles, vehicle) => ({
            ...vehicles,
            [vehicle.liveId]: { ...vehicle },
          }),
          {},
        )
      } catch {
        this.liveVehicles = []
      }
    },

    async fetchLastKnownLocation() {
      try {
        this.lastKnownLocation = await this.$services.spotTrackerLocationService.getLatestLocation(
          this.$props.id,
        )
      } catch {
        this.lastKnownLocation = null
      }
    },

    formatDate(date) {
      return !date ? 'Aucune date' : format(date, 'dd/MM/yyyy')
    },

    formatDateWithTime(dateString) {
      return !dateString ? 'Aucune date' : format(new Date(dateString), 'dd/MM/yyyy à HH:mm')
    },

    isPast(date) {
      return isPast(date)
    },
  },
}
</script>
